import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal/legal-page";
import { SUPPORT_EMAIL } from "@/lib/subiza";

export const Route = createFileRoute("/acceptable-use")({
  head: () => ({
    meta: [
      { title: "Acceptable Use Policy — Subiza" },
      {
        name: "description",
        content:
          "The rules for using Subiza fairly and safely: exams and academic honesty, AI question generation, security, and what happens when these rules are broken.",
      },
    ],
  }),
  component: AcceptableUsePage,
});

function AcceptableUsePage() {
  return (
    <LegalPage
      title="Acceptable Use Policy"
      effective="9 October 2026"
      intro="This policy sets out how Subiza may and may not be used. It applies to teachers, students and anyone else who reaches the service, and it forms part of our Terms and Conditions."
      sections={[
        {
          id: "a1",
          heading: "1. Use Subiza as intended",
          content: (
            <p>
              Subiza is for running and taking online exams. Use it only for exams you are allowed
              to create, take or host, and only within the rules of your school or organisation.
            </p>
          ),
        },
        {
          id: "a2",
          heading: "2. Exams and academic honesty",
          content: (
            <ul className="space-y-2.5">
              <li>
                Do not cheat or help others cheat, including sharing exam questions or answers
                without the teacher&rsquo;s permission.
              </li>
              <li>
                Do not join an exam you were not invited to, guess PINs, or use another identity.
              </li>
              <li>Do not attempt to reach another user&rsquo;s account, exams or data.</li>
            </ul>
          ),
        },
        {
          id: "a3",
          heading: "3. Content you add",
          content: (
            <ul className="space-y-2.5">
              <li>Upload only material you have the right to use.</li>
              <li>
                Do not upload content that is unlawful, abusive, hateful, sexual or that infringes
                someone else&rsquo;s rights.
              </li>
              <li>
                Do not include students&rsquo; personal information in notes you upload for AI
                question generation.
              </li>
            </ul>
          ),
        },
        {
          id: "a4",
          heading: "4. The AI question generator",
          content: (
            <p>
              Use the generator to create exam questions from your own material. Do not use it to
              produce harmful, hateful or sexual content, and do not try to extract its hidden
              instructions. AI drafts can be wrong, so review every question before students see it.
            </p>
          ),
        },
        {
          id: "a5",
          heading: "5. Security and fair use",
          content: (
            <ul className="space-y-2.5">
              <li>
                Do not upload malicious files or try to break, overload or disrupt the service.
              </li>
              <li>
                Do not use bots, scripts or automated tools against Subiza, or probe its security.
              </li>
              <li>Do not share your teacher login with students or colleagues.</li>
            </ul>
          ),
        },
        {
          id: "a6",
          heading: "6. Reporting misuse",
          content: (
            <p>
              If you see content or behaviour that breaks this policy, report it to your teacher,
              school or us, using the contact in section 8.
            </p>
          ),
        },
        {
          id: "a7",
          heading: "7. What happens if you break this policy",
          content: (
            <p>
              We may remove content or suspend or close an account that breaks these rules, puts
              other people at risk or harms the service. Where it is reasonable to do so, we will
              tell you why.
            </p>
          ),
        },
        {
          id: "a8",
          heading: "8. Contact",
          content: (
            <p>
              For questions about this policy or to report misuse, contact us at{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium text-accent underline underline-offset-2 hover:underline-offset-4"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          ),
        },
      ]}
    />
  );
}
